import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import {
  SegmentDensity,
  TripEvaluationResult,
  TripSegment,
  TripThresholds,
} from "../../map-asset/type";

const EARTH_RADIUS_METERS = 6371000; // Earth Radius

const MAX_SPEED_KMPH = 140; // GPS anomaly threshold

const MAX_TIME_GAP_MS = 3 * 60 * 1000; // Max idle time threshold (3 minutes)

const MIN_VALID_DISTANCE_METERS = 0.5;

/* 
If any single gap between two points exceeds this, Snap-to-Road cannot reliably
 connect them, segment is forced to SPARSE to avoid rendering incorrect routes*/
const SAFE_SNAP_LIMIT_METERS = 1000; // 1 km

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const roundToTwo = (value: number) => Number(value.toFixed(2));

const calcAverage = (numbers: number[]) =>
  numbers.length
    ? numbers.reduce((sum, val) => sum + val, 0) / numbers.length
    : 0;

// Haversine formula
const calcDistance = (
  pointA: VehicleMapDataPoint,
  pointB: VehicleMapDataPoint
) => {
  const dLat = toRadians(pointB.latitude - pointA.latitude);
  const dLng = toRadians(pointB.longitude - pointA.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(pointA.latitude)) *
      Math.cos(toRadians(pointB.latitude)) *
      Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_METERS * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

/**
 * Returns the value at a given percentile from a pre-sorted array.
 * Uses linear interpolation when the index falls between two values.
 */
const calcPercentile = (sortedValues: number[], target: number) => {
  if (!sortedValues.length) return 0;
  const index = (target / 100) * (sortedValues.length - 1);
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);
  if (lowerIndex === upperIndex) return sortedValues[lowerIndex];
  return (
    sortedValues[lowerIndex] +
    (sortedValues[upperIndex] - sortedValues[lowerIndex]) * (index - lowerIndex)
  );
};

const getSegmentDensity = (
  avgDistance: number,
  maxDistance: number,
  thresholds: TripThresholds
): SegmentDensity => {
  /*
  Exceeding this distance breaks Snap-to-Road. We override to SPARSE
  to ensure accurate route rendering
  */
  if (maxDistance > SAFE_SNAP_LIMIT_METERS) return "SPARSE";

  if (avgDistance <= thresholds.denseLimitMeters) return "DENSE";
  if (avgDistance <= thresholds.moderateLimitMeters) return "MODERATE";
  return "SPARSE";
};

/**
 * Evaluates a raw array of GPS trip points and breaks them into
 * meaningful segments based on distance, speed, and time gaps.
 *
 * Each segment is labeled with a density status (DENSE / MODERATE / SPARSE)
 *
 * @param tripPoints - Ordered array of GPS points with lat, lng, and timestamp
 * @returns TripEvaluationResult containing thresholds and labeled segments
 */
export function evaluateTrip(
  tripPoints: VehicleMapDataPoint[]
): TripEvaluationResult {
  // Return early if there are not enough points to form a segment
  const emptyResult: TripEvaluationResult = {
    totalPoints: tripPoints.length,
    totalTransitions: 0,
    thresholds: {
      denseLimitMeters: 0,
      moderateLimitMeters: 0,
      splitDistanceMeters: 0,
    },
    segments: [],
  };

  if (tripPoints.length < 2) return emptyResult;

  const validDistances: number[] = [];
  for (let i = 1; i < tripPoints.length; i++) {
    const distance = calcDistance(tripPoints[i - 1], tripPoints[i]);
    if (distance > MIN_VALID_DISTANCE_METERS) validDistances.push(distance);
  }

  if (!validDistances.length) return emptyResult;

  const sortedDistances = [...validDistances].sort((a, b) => a - b);

  const thresholds: TripThresholds = {
    denseLimitMeters: roundToTwo(calcPercentile(sortedDistances, 50)), // median — below this is DENSE
    moderateLimitMeters: roundToTwo(calcPercentile(sortedDistances, 75)), // 75th percentile — below this is MODERATE
    splitDistanceMeters: roundToTwo(calcPercentile(sortedDistances, 90)), // 90th percentile — above this triggers a segment split
  };

  /*
   * Build segments
   * Split into a new segment if any of these are true:
   *   - Distance gap too large (GPS jump)
   *   - Time gap > 3 min (vehicle stopped/offline)
   *   - Speed > 140 km/h (data anomaly)
   */
  const segments: TripSegment[] = [];
  let segStartIndex = 0;
  let segPoints: VehicleMapDataPoint[] = [tripPoints[0]];
  let segDistances: number[] = [];
  let segSpeeds: number[] = [];

  const saveSegment = (endIndex: number) => {
    if (segPoints.length < 2) return;
    const avgDistance = calcAverage(segDistances);
    const maxDistance = Math.max(...segDistances, 0);
    const density =
      segPoints.length <= 3
        ? "DENSE"
        : getSegmentDensity(avgDistance, maxDistance, thresholds);

    segments.push({
      startIndex: segStartIndex,
      endIndex,
      avgDistanceMeters: roundToTwo(avgDistance),
      maxDistanceMeters: roundToTwo(maxDistance),
      avgSpeedKmph: roundToTwo(calcAverage(segSpeeds)),
      densityStatus: density,
      points: segPoints,
    });
  };

  for (let i = 1; i < tripPoints.length; i++) {
    const prevPoint = tripPoints[i - 1];
    const currPoint = tripPoints[i];

    const distance = calcDistance(prevPoint, currPoint);
    const timeGap = (currPoint.timestamp - prevPoint.timestamp) * 1000;
    const speed = timeGap > 0 ? (distance / (timeGap / 1000)) * 3.6 : 0;

    // Check if this gap is too large to continue the current segment
    const shouldSplit =
      distance > thresholds.splitDistanceMeters ||
      timeGap > MAX_TIME_GAP_MS ||
      speed > MAX_SPEED_KMPH;

    if (shouldSplit) {
      saveSegment(i - 1);
      // Overlap one point at the boundary to preserve continuity across segments.
      segStartIndex = i - 1;
      segPoints = [prevPoint, currPoint];
      segDistances = [distance];
      segSpeeds = [speed];
    } else {
      segPoints.push(currPoint);
      segDistances.push(distance);
      segSpeeds.push(speed);
    }
  }
  saveSegment(tripPoints.length - 1);

  return {
    totalPoints: tripPoints.length,
    totalTransitions: tripPoints.length - 1,
    thresholds,
    segments,
  };
}
